import {
  AppUpdate,
  AppUpdateAvailability,
  AppUpdateInfo,
  AppUpdateResult,
  AppUpdateResultCode,
  FlexibleUpdateInstallStatus,
  FlexibleUpdateState,
} from '@capawesome/capacitor-app-update';

import Logger from './Logger';
import { isAndroidApp, isCordova } from './platform';

const IOS_APP_STORE_ID = '655695313';

export const getAppUpdateInfo = async (): Promise<AppUpdateInfo | null> => {
  if (!isCordova()) {
    return null;
  }

  try {
    return await AppUpdate.getAppUpdateInfo();
  } catch (error) {
    Logger.error('AppUpdate: Error getting update info', error);
    return null;
  }
};

export const getCurrentAppVersion = async (): Promise<string | null> => {
  const info = await getAppUpdateInfo();
  if (!info) return null;

  return isAndroidApp()
    ? String(info.currentVersionCode)
    : info.currentVersionName;
};

export const getAvailableAppVersion = async (): Promise<string | null> => {
  const info = await getAppUpdateInfo();
  if (!info) return null;

  return isAndroidApp()
    ? String(info.availableVersionCode)
    : info.availableVersionName;
};

export const isUpdateAvailable = (info: AppUpdateInfo | null): boolean => {
  if (!info) return false;
  return info.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE;
};

export const openAppStore = async (): Promise<void> => {
  try {
    await AppUpdate.openAppStore({
      appId: IOS_APP_STORE_ID,
    });
  } catch (error) {
    Logger.error('AppUpdate: Error opening app store', error);
    throw error;
  }
};

export const performImmediateUpdate =
  async (): Promise<AppUpdateResult | null> => {
    const info = await getAppUpdateInfo();

    if (!info || !isUpdateAvailable(info)) {
      return null;
    }

    if (!info.immediateUpdateAllowed) {
      Logger.warn('AppUpdate: Immediate update not allowed');
      return null;
    }

    try {
      return await AppUpdate.performImmediateUpdate();
    } catch (error) {
      Logger.error('AppUpdate: Error performing immediate update', error);
      throw error;
    }
  };

export const startFlexibleUpdate =
  async (): Promise<AppUpdateResult | null> => {
    const info = await getAppUpdateInfo();

    if (!info || !isUpdateAvailable(info)) {
      return null;
    }

    if (!info.flexibleUpdateAllowed) {
      Logger.warn('AppUpdate: Flexible update not allowed');
      return null;
    }

    try {
      return await AppUpdate.startFlexibleUpdate();
    } catch (error) {
      Logger.error('AppUpdate: Error starting flexible update', error);
      throw error;
    }
  };

export const completeFlexibleUpdate = async (): Promise<void> => {
  try {
    await AppUpdate.completeFlexibleUpdate();
  } catch (error) {
    Logger.error('AppUpdate: Error completing flexible update', error);
    throw error;
  }
};

export const addFlexibleUpdateListener = async (
  callback: (state: FlexibleUpdateState) => void,
): Promise<void> => {
  await AppUpdate.addListener('onFlexibleUpdateStateChange', callback);
};

export const removeAllListeners = async (): Promise<void> => {
  await AppUpdate.removeAllListeners();
};

export {
  AppUpdateAvailability,
  AppUpdateResultCode,
  FlexibleUpdateInstallStatus,
};
export type { AppUpdateInfo, AppUpdateResult, FlexibleUpdateState };
